import { useReducer } from "react";
import { useNavigate } from "react-router-dom";

import { camelToKebab } from "../components/Onboarding/utils/stringUtils";
import { setUrlParams } from "../components/Onboarding/utils/urlParams";

interface StepNavigationState {
  currentStep: string;
  previousStep: string | null;
  history: string[];
  userFlow: "vet" | "non-vet" | null;
}

type StepNavigationAction =
  | { type: "NAVIGATE_TO_STEP"; step: string }
  | { type: "NAVIGATE_FROM_ROLE"; isVet: boolean }
  | { type: "NAVIGATE_FROM_TEMPLATE_CHOICE"; templateType: string }
  | { type: "SKIP_TO_STEP"; targetStep: string }
  | { type: "GO_BACK" }
  | { type: "SYNC_TO_STEP"; step: string }
  | { type: "NAVIGATE_TEAM_FLOW"; targetStep: string }
  | { type: "RESET_NAVIGATION"; initialStep?: string };

const stepNavigationReducer = (
  state: StepNavigationState,
  action: StepNavigationAction,
): StepNavigationState => {
  switch (action.type) {
    case "NAVIGATE_TO_STEP": {
      const newHistory = state.currentStep
        ? [...state.history, state.currentStep]
        : state.history;
      return {
        ...state,
        currentStep: action.step,
        previousStep: state.currentStep,
        history: newHistory,
      };
    }

    case "NAVIGATE_FROM_ROLE": {
      return {
        ...state,
        userFlow: action.isVet ? "vet" : "non-vet",
      };
    }

    case "NAVIGATE_FROM_TEMPLATE_CHOICE": {
      let nextStepFromTemplate;
      if (
        action.templateType === "soap" ||
        action.templateType === "non-soap"
      ) {
        nextStepFromTemplate = "template-setup";
      } else {
        nextStepFromTemplate = "scribenote-setup";
      }
      const newHistory = state.currentStep
        ? [...state.history, state.currentStep]
        : state.history;
      return {
        ...state,
        currentStep: nextStepFromTemplate,
        previousStep: state.currentStep,
        history: newHistory,
      };
    }

    case "SKIP_TO_STEP": {
      const newHistory = state.currentStep
        ? [...state.history, state.currentStep]
        : state.history;
      return {
        ...state,
        currentStep: action.targetStep,
        previousStep: state.currentStep,
        history: newHistory,
      };
    }

    case "GO_BACK": {
      const previousStep =
        state.history[state.history.length - 1] || state.previousStep;
      const newHistory = state.history.slice(0, -1);
      return {
        ...state,
        currentStep: previousStep || "welcome",
        previousStep: newHistory[newHistory.length - 1] || null,
        history: newHistory,
      };
    }

    case "SYNC_TO_STEP": {
      return {
        ...state,
        currentStep: action.step,
      };
    }

    case "NAVIGATE_TEAM_FLOW": {
      const newHistory = state.currentStep
        ? [...state.history, state.currentStep]
        : state.history;
      return {
        ...state,
        currentStep: action.targetStep,
        previousStep: state.currentStep,
        history: newHistory,
      };
    }

    case "RESET_NAVIGATION":
      return {
        currentStep: action.initialStep || "welcome",
        previousStep: null,
        history: [],
        userFlow: null,
      };

    default:
      return state;
  }
};

export const useStepNavigation = (initialStep = "welcome") => {
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(stepNavigationReducer, {
    currentStep: initialStep,
    previousStep: null,
    history: [],
    userFlow: null,
  });

  const navigateToStep = (
    step: string,
    params: Record<string, unknown> = {},
  ) => {
    if (step === "__BACK__") {
      dispatch({ type: "GO_BACK" });
      window.history.back();
      return;
    }
    const { from: _from, ...otherParams } = params || {};
    const clearedParams = { ...otherParams, from: null };
    setUrlParams(clearedParams, true);

    dispatch({ type: "NAVIGATE_TO_STEP", step });

    const url = `/onboarding/${camelToKebab(step)}`;
    const search = window.location.search || "";
    navigate(`${url}${search}`, { replace: false });
  };

  const navigateFromRole = (isVet: boolean) => {
    dispatch({ type: "NAVIGATE_FROM_ROLE", isVet });
  };

  const navigateFromTemplateChoice = (templateType: string) => {
    dispatch({
      type: "NAVIGATE_FROM_TEMPLATE_CHOICE",
      templateType,
    });
  };

  const skipToStep = (targetStep: string) => {
    dispatch({ type: "SKIP_TO_STEP", targetStep });
  };

  const goBack = () => {
    const previous =
      state.history[state.history.length - 1] ||
      state.previousStep ||
      "welcome";
    dispatch({ type: "GO_BACK" });
    setUrlParams({ from: null }, true);
    const url = `/onboarding/${camelToKebab(previous)}`;
    const search = window.location.search || "";
    navigate(`${url}${search}`, { replace: true });
  };

  const resetNavigation = (initialStep?: string) => {
    dispatch({ type: "RESET_NAVIGATION", initialStep });
  };

  const navigateTeamFlow = (targetStep: string) => {
    dispatch({ type: "NAVIGATE_TEAM_FLOW", targetStep });
  };

  const syncToStep = (step: string) => {
    dispatch({ type: "SYNC_TO_STEP", step });
  };

  const canGoBack =
    state.history.length > 0 || state.previousStep !== null;

  const calculateProgress = () => {
    const vetSteps = [
      "welcome",
      "role",
      "template-choice",
      "template-setup",
      "cod-setup",
      "referral-source",
      "group-name",
      "finish",
    ];
    const nonVetSteps = [
      "welcome",
      "role",
      "team-setup-choice",
      "team-create",
      "team-add-users",
      "team-confirm",
      "team-receipt",
      "referral-source",
      "group-name",
      "finish",
    ];

    const isNonVetLike = state.currentStep?.startsWith("team-");
    const flow = state.userFlow || (isNonVetLike ? "non-vet" : "vet");
    const steps = flow === "non-vet" ? nonVetSteps : vetSteps;

    const currentIndex = steps.indexOf(state.currentStep);
    if (currentIndex === -1) return 0;
    return Math.round(((currentIndex + 1) / steps.length) * 100);
  };

  return {
    // State
    currentStep: state.currentStep,
    previousStep: state.previousStep,
    userFlow: state.userFlow,
    history: state.history,

    // Computed
    canGoBack,
    progress: calculateProgress(),

    // Actions
    navigateToStep,
    navigateFromRole,
    navigateFromTemplateChoice,
    navigateTeamFlow,
    syncToStep,
    skipToStep,
    goBack,
    resetNavigation,
  };
};
